import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classNames from 'classnames';
import { DirectionalHint, TooltipHost } from '@fluentui/react';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { SelectedItem } from '@bingads-webui-campaign-react/pill-item-components';

const getStyles = (_components, { palette }) => ({
  OperationButton: {
    height: 32,
    color: palette.themePrimary,
  },
  SelectedBrandListsTagsHeader: {
    padding: 8,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  SelectedBrandListsTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gridGap: 8,
    marginBottom: 12,
  },
  BrandListItem: {
    padding: '12px 0 12px 12px',
    display: 'flex',
    alignItems: 'center',
    '&.disabled': {
      padding: 12,
    },
  },
  AndMoreText: {
    fontSize: 14,
    lineHeight: '34px',
  },
});

const BRAND_LIST_NAME_DISPLAY_LIMIT = 20;

const convertToSubstring = (str, length) => {
  if (str.length <= length) {
    return str;
  }
  return `${str.substring(0, length)}...`;
};

export const SelectedBrandListsTags = ({
  className,
  selectedOptions,
  setSelectedOptions,
  isDisabled,
}) => {
  const classes = useDefaultStyles(getStyles);

  const onRemoveItem = useCallback((index) => {
    const newSelectedOptions = _.clone(selectedOptions);
    newSelectedOptions.splice(index, 1);
    setSelectedOptions(newSelectedOptions);
  }, [selectedOptions, setSelectedOptions]);

  const onRenderSelectedItemText = useCallback(({ item, text }) => (
    <TooltipHost
      id={`selected-item-tooltip-${item.key}`}
      directionalHint={DirectionalHint.rightCenter}
      content={text}
      style={{ root: { display: 'inline-block' } }}
    >
      <div className={classNames('brand-list-tag-item', classes.BrandListItem, isDisabled ? 'disabled' : '')}>
        {convertToSubstring(text, BRAND_LIST_NAME_DISPLAY_LIMIT)}
      </div>
    </TooltipHost>
  ), [classes.BrandListItem, isDisabled]);

  return (
    <div className={classNames(classes.SelectedBrandListsTagsContainer, 'selected-brand-lists-tags-container', className)}>
      <div className={classNames(classes.SelectedBrandListsTags, 'selected-brand-lists-tags')}>
        {!_.isEmpty(selectedOptions) && selectedOptions.map((item, index) => (
          <SelectedItem
            key={item.key}
            itemText={item.Name}
            onRenderText={text => onRenderSelectedItemText({ item, text })}
            itemIndex={index}
            onDelete={isDisabled ? undefined : onRemoveItem}
          />
        ))}
      </div>
    </div>
  );
};

SelectedBrandListsTags.propTypes = {
  className: PropTypes.string,
  selectedOptions: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    brandCount: PropTypes.number,
  })).isRequired,
  setSelectedOptions: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
};

SelectedBrandListsTags.defaultProps = {
  className: undefined,
  isDisabled: false,
};
